import { SEOHead } from '@/features/shared/components/SEOHeader/SEOHead'
import { SEO_PAGE_TYPE, type SEOHeadProps } from '@/features/shared/components/SEOHeader/types'
import { formatProductPageTitle } from '@/features/shared/utils/formatter'
import { type Product } from '@/features/shop/services/Product/types'
import { getProductPageUrl } from '@/features/shared/utils/getProductPageUrl'

type ProductSEOProps = Pick<
  Product,
  'review_stats' | 'name' | 'universal_id' | 'brandName' | 'price' | 'image' | 'id'
> & { breadcrumbs?: SEOHeadProps['breadcrumbs'] }

export const ProductSEOHead = ({
  id,
  review_stats,
  brandName,
  name,
  universal_id,
  price,
  image,
  breadcrumbs,
}: ProductSEOProps) => {
  const seoImage = {
    url: image.url,
    width: undefined,
    height: undefined,
  }

  const reviewStats = review_stats
  const showRatingsAndReviews = !!reviewStats?.rating_distribution
  const pageTitle = formatProductPageTitle(name, 'Shipt')
  const pageDescription = `Buy ${name} online with quick same day delivery to your door. Shop with Shipt`

  const canonicalUrl = universal_id
    ? `https://www.shipt.com${getProductPageUrl({
        universal_id,
        ungated: true,
      })}`
    : `https://www.shipt.com${getProductPageUrl({
        id,
        universal_id,
        ungated: false,
      })}`

  const productSchema = {
    '@type': [SEO_PAGE_TYPE.PRODUCT, SEO_PAGE_TYPE.WEB_PAGE],
    '@context': 'https://schema.org',
    name,
    description: pageDescription,
    url: canonicalUrl,
    image: seoImage,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    brand: {
      '@type': 'Brand',
      name: brandName || 'Shipt',
      logo: {
        '@type': 'ImageObject',
        url: image.url,
        width: 60,
        height: 60,
      },
    },
    aggregateRating:
      showRatingsAndReviews && reviewStats.total_ratings > 0
        ? {
            '@type': 'AggregateRating',
            ratingValue: reviewStats.average_rating,
            ratingCount: reviewStats.total_reviews,
          }
        : undefined,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'USD',
      price: price.toFixed(2),
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      url: canonicalUrl,
      priceValidUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 1))
        .toISOString()
        .split('T')[0],
      seller: {
        '@type': 'Organization',
        name: 'Shipt',
      },
    },
  }

  const socialMediaSchema = {
    title: pageTitle,
    description: pageDescription,
    url: canonicalUrl,
    type: 'product',
    image: seoImage,
    card: 'summary_large_image',
    price: price.toFixed(2),
  }
  return (
    <SEOHead
      title={pageTitle}
      description={pageDescription}
      canonicalUrl={canonicalUrl}
      breadcrumbs={breadcrumbs}
      image={seoImage}
      pageTypeSchema={productSchema}
      socialMediaSchema={socialMediaSchema}
    />
  )
}
